<script setup>
import { useSeo } from '@/composables/useSeo'

useSeo('technology')
import { ref, onMounted } from 'vue'
import {
  CommandLineIcon,
  KeyIcon,
  BookOpenIcon,
  SignalIcon,
  QrCodeIcon,
  LanguageIcon,
  ServerStackIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/vue/24/outline'

// Hero terminal — request / response tabs
const activeTab = ref('request')
const codeSnippet = {
  request: `curl https://api.bifrost.events/api/v1/events/42/attendees \\
  -H "Authorization: Bearer $ACCESS_TOKEN" \\
  -H "Accept-Language: fr"`,
  response: `{
  "data": [
    {
      "id": "att_9f27",
      "first_name": "Nadia",
      "last_name": "B.",
      "status": "checked_in",
      "checked_in_at": "2026-03-14T09:12:44Z"
    }
  ]
}`
}

// Stack grid — copy lives in i18n (tech.stack.items), icons here
const stackIcons = [CommandLineIcon, KeyIcon, BookOpenIcon, SignalIcon, QrCodeIcon, LanguageIcon]

// Integration grid — copy in i18n (tech.integration.items), icons here
const integrationIcons = [BookOpenIcon, CommandLineIcon, ServerStackIcon]

onMounted(() => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('animate-reveal')
        observer.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1 })

  document.querySelectorAll('.reveal-on-scroll').forEach((el) => {
    observer.observe(el)
  })
})
</script>

<template>
  <!-- ================= HERO ================= -->
  <header class="bg-slate-950 pt-36 pb-24">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">

        <!-- Text -->
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 ease-out">
          <div
            class="inline-flex items-center px-3 py-1.5 border border-white/15 text-slate-300 text-xs font-mono tracking-wide mb-10 rounded-md">
            <span class="flex h-1.5 w-1.5 rounded-full bg-primary-400 me-2"></span>
            {{ $t('tech.badge') }}
          </div>

          <h1 class="text-4xl md:text-6xl font-semibold text-white leading-[1.08]">
            {{ $t('tech.hero.title') }}<br />
            <span class="text-primary-400">{{ $t('tech.hero.accent') }}</span>
          </h1>

          <p class="mt-8 text-lg text-slate-400 leading-relaxed">
            {{ $t('tech.hero.desc') }}
          </p>

          <div class="mt-10 flex flex-col sm:flex-row gap-3">
            <a href="https://docs.bifrost.events" target="_blank"
              class="px-7 py-3.5 bg-white text-slate-950 rounded-md font-semibold text-base hover:bg-slate-200 transition text-center">
              {{ $t('tech.hero.primary') }}
            </a>
            <a href="https://bifrost.events/touch" target="_blank"
              class="px-7 py-3.5 border border-white/20 text-white rounded-md font-semibold text-base hover:bg-white/10 transition text-center">
              {{ $t('tech.hero.secondary') }}
            </a>
          </div>
        </div>

        <!-- Terminal -->
        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 delay-150 min-w-0" dir="ltr">
          <div class="rounded-lg border border-white/10 overflow-hidden max-w-full">
            <div class="bg-slate-900 px-3 py-2 flex items-center gap-2 border-b border-white/10">
              <div class="flex gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
                <div class="w-2.5 h-2.5 rounded-full bg-slate-700"></div>
              </div>
              <div class="mx-auto text-xs text-slate-500 font-mono">api.bifrost.events</div>
            </div>

            <div class="flex bg-slate-900 border-b border-white/10">
              <button @click="activeTab = 'request'"
                class="px-4 py-2 text-xs font-mono transition border-b-2"
                :class="activeTab === 'request' ? 'text-white border-primary-400 bg-slate-950' : 'text-slate-500 border-transparent hover:text-slate-300'">
                request.sh
              </button>
              <button @click="activeTab = 'response'"
                class="px-4 py-2 text-xs font-mono transition border-b-2"
                :class="activeTab === 'response' ? 'text-white border-primary-400 bg-slate-950' : 'text-slate-500 border-transparent hover:text-slate-300'">
                response.json
              </button>
            </div>

            <div class="p-4 sm:p-6 bg-slate-950 min-h-[260px] overflow-x-auto">
              <pre class="font-mono text-xs sm:text-sm leading-relaxed text-slate-300 text-left"><code>{{ activeTab === 'request' ? codeSnippet.request : codeSnippet.response }}</code></pre>
            </div>
          </div>
        </div>

      </div>
    </div>
  </header>

  <!-- ================= STACK ================= -->
  <section class="py-28 bg-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('tech.stack.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('tech.stack.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('tech.stack.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <div v-for="(icon, i) in stackIcons" :key="i"
          class="group bg-white p-8 hover:bg-slate-50 transition-colors duration-200">
          <div class="flex items-start justify-between mb-6">
            <component :is="icon" class="w-7 h-7 text-slate-900" />
            <span class="text-[11px] font-mono text-slate-400 border border-slate-200 rounded px-2 py-0.5" dir="ltr">
              {{ $t(`tech.stack.items[${i}].tag`) }}
            </span>
          </div>
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t(`tech.stack.items[${i}].title`) }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t(`tech.stack.items[${i}].desc`) }}</p>
        </div>
      </div>
    </div>
  </section>

  <!-- ================= REALTIME / AUDIENCES ================= -->
  <section class="py-28 bg-slate-950 text-white">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="grid lg:grid-cols-2 gap-16 items-center">

        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
          <p class="text-xs font-mono text-primary-400 tracking-widest uppercase mb-4">{{ $t('tech.realtime.label') }}</p>
          <h2 class="text-4xl md:text-5xl font-semibold mb-6">{{ $t('tech.realtime.title') }}</h2>
          <p class="text-lg text-slate-400 leading-relaxed mb-8">{{ $t('tech.realtime.desc') }}</p>
          <ul class="space-y-4">
            <li v-for="i in 3" :key="i" class="flex items-start text-slate-300">
              <span class="mt-2 me-4 flex-shrink-0 h-1.5 w-1.5 rounded-full bg-primary-400"></span>
              {{ $t(`tech.realtime.points[${i - 1}]`) }}
            </li>
          </ul>
        </div>

        <div class="reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700 min-w-0" dir="ltr">
          <div class="rounded-lg border border-white/10 bg-slate-950 p-4 sm:p-6 font-mono text-xs sm:text-sm leading-relaxed overflow-x-auto text-left">
            <div class="text-slate-500 mb-2"># REST — audiences</div>
            <div class="text-slate-300"><span class="text-primary-400">GET</span> /api/v1/admin/&hellip;</div>
            <div class="text-slate-300"><span class="text-primary-400">GET</span> /api/v1/events/&hellip;</div>
            <div class="text-slate-300"><span class="text-primary-400">GET</span> /api/v1/attendee/&hellip;</div>
            <div class="text-slate-300"><span class="text-primary-400">GET</span> /api/v1/hostess/&hellip;</div>
            <div class="text-slate-500 mt-6 mb-2"># Websockets — broadcast channels</div>
            <div class="text-slate-300"><span class="text-primary-400">presence</span> event.{id}.attendance</div>
            <div class="text-slate-300"><span class="text-primary-400">channel&nbsp;</span> event.{id}.activity.results</div>
            <div class="text-slate-300"><span class="text-primary-400">channel&nbsp;</span> event.{id}.livestream.status</div>
          </div>
        </div>

      </div>
    </div>
  </section>

  <!-- ================= INTEGRATION ================= -->
  <section class="py-28 bg-slate-50 border-t border-slate-200">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="max-w-3xl mb-16 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <p class="text-xs font-mono text-primary-600 tracking-widest uppercase mb-4">{{ $t('tech.integration.label') }}</p>
        <h2 class="text-4xl md:text-5xl font-semibold text-slate-900 mb-5">{{ $t('tech.integration.title') }}</h2>
        <p class="text-lg text-slate-500">{{ $t('tech.integration.desc') }}</p>
      </div>

      <div
        class="grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200 reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">

        <div class="bg-white p-8">
          <BookOpenIcon class="w-7 h-7 text-slate-900 mb-6" />
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t('tech.integration.items[0].title') }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed mb-6">{{ $t('tech.integration.items[0].desc') }}</p>
          <a href="https://docs.bifrost.events" target="_blank"
            class="inline-flex items-center gap-2 text-sm font-mono text-primary-600 hover:underline" dir="ltr">
            docs.bifrost.events
            <ArrowTopRightOnSquareIcon class="w-3.5 h-3.5" />
          </a>
        </div>

        <div class="bg-white p-8">
          <CommandLineIcon class="w-7 h-7 text-slate-900 mb-6" />
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t('tech.integration.items[1].title') }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed mb-6">{{ $t('tech.integration.items[1].desc') }}</p>
          <span class="inline-block text-sm font-mono text-slate-500 border border-slate-200 rounded px-2 py-0.5" dir="ltr">
            api.bifrost.events/api/v1
          </span>
        </div>

        <div class="bg-white p-8">
          <ServerStackIcon class="w-7 h-7 text-slate-900 mb-6" />
          <h3 class="text-lg font-semibold text-slate-900 mb-2">{{ $t('tech.integration.items[2].title') }}</h3>
          <p class="text-sm text-slate-500 leading-relaxed">{{ $t('tech.integration.items[2].desc') }}</p>
        </div>

      </div>
    </div>
  </section>

  <!-- ================= CTA ================= -->
  <section class="py-24 bg-white">
    <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div
        class="bg-slate-950 rounded-lg p-12 md:p-20 text-center reveal-on-scroll opacity-0 translate-y-10 transition-all duration-700">
        <h2 class="text-3xl md:text-5xl font-semibold text-white mb-6">{{ $t('tech.cta.title') }}</h2>
        <p class="text-slate-400 text-lg mb-10 max-w-2xl mx-auto">{{ $t('tech.cta.desc') }}</p>
        <div class="flex flex-col sm:flex-row justify-center gap-3">
          <a href="https://bifrost.events/touch" target="_blank"
            class="px-7 py-3.5 bg-white text-slate-950 font-semibold rounded-md text-base hover:bg-slate-200 transition">
            {{ $t('tech.cta.primary') }}
          </a>
          <a href="https://docs.bifrost.events" target="_blank"
            class="px-7 py-3.5 border border-white/20 text-white font-semibold rounded-md text-base hover:bg-white/10 transition">
            {{ $t('tech.cta.secondary') }}
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style>
</style>
